import {
  Button,
  CSSObject,
  FormControlLabel,
  IconButton,
  Stack,
  styled,
  Switch,
  ToggleButton,
  Typography,
} from '@mui/material';
import { useCallback, useMemo } from 'react';
import { Control, Controller } from 'react-hook-form';

import { DynamicIcon, EllipsesMenu, FilterChips, Tooltip } from 'src/components';
import { SearchBar } from 'src/components/SearchBar/search-bar';
import {
  CustomViewFormConditionItem,
  CustomViewFormFields,
  CustomViewFormModel,
} from 'src/graphql/entities/custom-view';
import { useCustomViewFilterOptions } from 'src/graphql/entities/custom-view/custom-view-config/use-custom-view-options';
import { QueryOptionsValue } from 'src/pages/Settings/rules/use-rules-engine';
import { useGlobalStore } from 'src/store';
import { GridDensity } from 'src/store/custom-views';
import { useScreenDimensions } from 'src/utils/system';

export const DisplayButton = styled(Button, {
  name: 'DisplayButton',
  shouldForwardProp: (prop) => prop !== 'isIconButton',
})<{ isIconButton?: boolean }>(({ theme, isIconButton }) => {
  let styles: CSSObject = {
    padding: theme.spacing(0.2, 1),
    color: theme.palette.custom.OV.sys.onSurface,
    background: theme.palette.custom.OV.sys.surfaceContainerHighest,
    border: `1px solid ${theme.palette.custom.OV.sys.outline}`,
    boxShadow: 'none',
    '&:hover': {
      opacity: 0.8,
      background: theme.palette.custom.OV.sys.surfaceContainerHighest,
      border: `1px solid ${theme.palette.custom.OV.sys.outline}`,
      boxShadow: 'none',
    },
  };
  if (isIconButton) {
    styles = {
      ...styles,
      padding: theme.spacing(0.3),
      minWidth: 0,
      fontSize: 0,
      '.MuiButton-startIcon': { margin: 0 },
      '.MuiButton-startIcon>*:nth-of-type(1)': {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      },
    };
  }
  return styles;
});

export const ChipsContainer = styled(Stack)({
  flexDirection: 'row',
  gap: '0.6rem',
  flexWrap: 'wrap',
  alignItems: 'center',
  ml: '0.6rem',
});

export interface CustomViewsActionsHeaderProps {
  gridDensity: GridDensity | null;
  setGridDensity: (density: GridDensity) => void;
  conditions?: CustomViewFormConditionItem[];
  toggleQueryBuilder: () => void;
  deleteCondition: (id: string) => void;
  canBePrivate?: boolean;
  control?: Control<CustomViewFormModel, unknown>;
  setColumnsSelectorAnchorEl?: (el: HTMLButtonElement) => void;
  handleFilterChange?: (newFiltersState: CustomViewFormConditionItem[]) => void;
  resetToDefault?: () => void;
  showModifiedViewActions?: boolean;
  handleCopyClick?: () => void;
  handleUpdateClick?: () => void;
  handleSearch?: (value: string) => void;
}

const ICON_TO_DENSITY_MAP: Record<GridDensity, string> = {
  compact: 'density_small',
  comfortable: 'density_medium',
  standard: 'density_large',
};

export const CustomViewsActionsHeader = ({
  gridDensity,
  setGridDensity,
  conditions: rawConditions,
  toggleQueryBuilder,
  deleteCondition,
  canBePrivate,
  control,
  setColumnsSelectorAnchorEl,
  handleFilterChange,
  showModifiedViewActions,
  resetToDefault,
  handleCopyClick,
  handleUpdateClick,
  handleSearch,
}: CustomViewsActionsHeaderProps) => {
  const { isMobile, isDesktop } = useScreenDimensions();
  const selectedPartnerId = useGlobalStore.use.selectedPartnerId();

  const conditions = useMemo(
    () => rawConditions?.filter((c) => c.field?.value) ?? [],
    [rawConditions],
  );

  const queryOptions = useCustomViewFilterOptions({
    partnerId: selectedPartnerId,
    conditions: rawConditions,
  });

  const handleDensityChange = useCallback(() => {
    if (gridDensity === 'standard') {
      setGridDensity('compact');
    }

    if (gridDensity === 'compact') {
      setGridDensity('comfortable');
    }

    if (gridDensity === 'comfortable') {
      setGridDensity('standard');
    }
  }, [setGridDensity, gridDensity]);

  const filtersButton = !conditions.length ? (
    <Button
      sx={{ color: (theme) => theme.palette.custom.OV.sys.onSurface }}
      variant="text"
      onClick={toggleQueryBuilder}
      startIcon={<DynamicIcon name="filter_list" />}
    >
      FILTER
    </Button>
  ) : (
    <IconButton onClick={toggleQueryBuilder}>
      <DynamicIcon name="filter_list" />
    </IconButton>
  );

  const modifiedViewActionMenuItems = useMemo(() => {
    if (!showModifiedViewActions) {
      return [];
    }

    const menuItems = [];

    if (handleCopyClick) {
      menuItems.push({
        label: 'Save as new view',
        icon: 'add',
        onClick: handleCopyClick,
      });
    }

    if (handleUpdateClick) {
      menuItems.push({
        label: 'Save updates for everyone',
        icon: 'save',
        onClick: handleUpdateClick,
      });
    }

    return menuItems;
  }, [showModifiedViewActions, handleUpdateClick, handleCopyClick]);

  return (
    <Stack
      alignItems="flex-start"
      justifyContent="space-between"
      sx={{
        flexDirection: {
          xs: 'column',
          sm: 'row',
        },
        gap: {
          xs: '0.5rem',
          sm: '0.5rem',
        },
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        sx={{ minWidth: { xs: 0 }, width: { xs: '100%', sm: 'auto' } }}
      >
        {isDesktop ? filtersButton : null}

        <ChipsContainer sx={{ minWidth: { xs: 0 }, flex: { xs: 1 } }}>
          <FilterChips
            conditions={conditions}
            onConditionsChange={handleFilterChange}
            deleteCondition={deleteCondition}
            queryOptions={queryOptions as Record<string, QueryOptionsValue>}
            chipsContainerProps={
              isMobile
                ? {
                    sx: {
                      flexWrap: 'nowrap',
                      overflowX: 'auto',
                      width: '100%',
                      minWidth: 0,
                      scrollbarWidth: 'none',
                      '&::-webkit-scrollbar': { display: 'none' },
                      '& > *': { flexShrink: 0 },
                    },
                  }
                : undefined
            }
          />

          {!!resetToDefault && (
            <Tooltip title="Reset to default state">
              <Button
                onClick={resetToDefault}
                sx={{ transform: 'scale(0.75)' }}
                size="small"
                color="primary"
                variant="text"
              >
                Reset View
              </Button>
            </Tooltip>
          )}
          {!!modifiedViewActionMenuItems.length && (
            <EllipsesMenu options={modifiedViewActionMenuItems} />
          )}
        </ChipsContainer>
      </Stack>

      <Stack
        direction="row"
        alignItems="center"
        flexWrap="wrap"
        sx={{
          order: { xs: -1, sm: 1 },
          alignSelf: 'flex-end',
          gap: {
            xs: '0.5rem',
            sm: '1rem',
          },
        }}
      >
        {isMobile ? filtersButton : null}

        {canBePrivate && control ? (
          <Controller
            name={CustomViewFormFields.IsPrivate}
            control={control}
            render={({ field }) => (
              <FormControlLabel
                control={<Switch checked={field.value} {...field} />}
                label={
                  <Typography variant="body2" whiteSpace="nowrap">
                    Private View
                  </Typography>
                }
              />
            )}
          />
        ) : null}
        {handleSearch ? (
          <SearchBar
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              handleSearch(e.target.value);
            }}
          />
        ) : null}

        <DisplayButton
          variant="contained"
          isIconButton={isMobile}
          startIcon={<DynamicIcon name="tune" fontSize="10rem" margin={0} />}
          onClick={(e) => setColumnsSelectorAnchorEl?.(e?.currentTarget)}
          sx={{
            fontSize: {
              xs: 0,
              sm: '0.75rem',
            },
          }}
        >
          DISPLAY
        </DisplayButton>
        {!!gridDensity && (
          <Tooltip title="Change row spacing">
            <ToggleButton
              value={gridDensity}
              size="small"
              onClick={handleDensityChange}
              sx={{ padding: '0.2rem' }}
            >
              <DynamicIcon name={ICON_TO_DENSITY_MAP[gridDensity]} />
            </ToggleButton>
          </Tooltip>
        )}
      </Stack>
    </Stack>
  );
};
